import type { BusinessUnit } from '@/types/businessUnitTypes';
import type { Organization } from '@/types/organization';
import type { SafeScarfUser } from '@/types/safeScarf';

interface TargetUser {
  userId: string | undefined;
  businessUnit: BusinessUnit;
  createdBy: string;
  productId: string;
  userProducts: string[];
  osProjectType: string;
  userToBeUpdatedEmail?: string;
  userEmail: string;
  userToBeDeletedEmail?: string;
  userToBeDeletedRole: string;
  userToBeDeletedRoles?: string[];
  userToBeUpdatedRole: string;
  rowEmail?: string;
}
type Members = Record<
  string,
  {
    role: number;
    [key: string]: unknown;
  }
>;

interface SafescarfRule {
  safescarfUser: SafeScarfUser;
  product: {
    members: Members;
    [key: string]: unknown;
  };
  productType: {
    members: Members;
    [key: string]: unknown;
  };
}

const MONITORING_ORGANIZATION =
  window.PORTAL_MONITORING_ORGANIZATION ?? import.meta.env.VITE_MONITORING_ORGANIZATION;
export const APPROVED_CALC_EXCLUSION_ROLES = ['ADMIN', 'SUPPORT'];
export const ADMIN_ORG_ALLOWED_USER_ROLES = ['ADMIN', 'SUPPORT', 'CAAS_ADMIN'];
const AO_AA_BUM_TL_DEV = [
  'ACCOUNT_OWNER',
  'ACCOUNT_ASSISTANT',
  'BUSINESS_UNIT_MANAGER',
  'TECHNICAL_LEAD',
  'DEVELOPER',
];
const AA_BUM_TL_DEV = ['ACCOUNT_ASSISTANT', 'BUSINESS_UNIT_MANAGER', 'TECHNICAL_LEAD', 'DEVELOPER'];
const BUM_TL_DEV = ['BUSINESS_UNIT_MANAGER', 'TECHNICAL_LEAD', 'DEVELOPER'];
const TL_DEV = ['TECHNICAL_LEAD', 'DEVELOPER'];
const rules = {
  ADMIN: {
    static: [
      '/organizations:VIEW_ALL',
      '/organizations:VIEW_ALL_DETAILS',
      '/organization:POST',
      '/organization:PUT',
      '/reports',
      '/marketplace:VIEW',
      '/marketplace:PUT',
      '/forecast:VIEW',
      '/users:VIEW',
      '/users:POST',
      '/users:PUT:ORG',
      '/business-units:VIEW',
      '/business-units:POST',
      '/business-units:PUT',
      '/business-units:DELETE',
      '/business-units:PUT:BUM',
      '/projects:VIEW',
      '/projects:POST',
      '/projects:PATCH',
      '/projects:DELETE',
      '/osprojects:DELETE',
      '/osprojects:PATCH',
      '/products:VIEW',
      '/products:POST',
      '/products:PUT',
      '/products:PUT:TAGS',
      '/products:CONTACTS_VIEW',
      '/products:CONTACTS_PATCH',
      '/products:CONTACTS_DELETE',
      '/products:DELETE',
      '/products/safescarf/machine-accounts:VIEW',
      '/products:assign',
      '/reports/caas-clusters:PATCH',
    ],
    dynamic: {
      '/users:PUT': (data: TargetUser) => data.userToBeUpdatedEmail !== data.userEmail,
      '/users:DELETE': (data: TargetUser) => data.userToBeDeletedEmail !== data.userEmail,
      '/users:PUT_ROLE_CHECK': (enterpriseName: string) =>
        enterpriseName === 'admin-org' ? ADMIN_ORG_ALLOWED_USER_ROLES : AO_AA_BUM_TL_DEV,
      '/monitoring-dashboards': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Monitoring') !== undefined,
    },
  },
  SUPPORT: {
    static: [
      '/organizations:VIEW_ALL',
      '/reports',
      '/users:VIEW',
      '/business-units:VIEW',
      '/marketplace:VIEW',
      '/projects:VIEW',
      '/products:VIEW',
      '/products:CONTACTS_VIEW',
    ],
    dynamic: {
      '/monitoring-dashboards': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Monitoring') !== undefined,
    },
  },
  CAAS_ADMIN: {
    static: [
      '/organizations:VIEW_ALL',
      '/reports',
      '/users:VIEW',
      '/business-units:VIEW',
      '/marketplace:VIEW',
      '/projects:VIEW',
      '/products:VIEW',
      '/products:CONTACTS_VIEW',
      '/reports/caas-clusters:PATCH',
    ],
    dynamic: {
      '/monitoring-dashboards': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Monitoring') !== undefined,
    },
  },
  ACCOUNT_OWNER: {
    static: [
      '/organizations:VIEW_OWN_DETAILS',
      '/users:VIEW',
      '/users:POST',
      '/business-units:VIEW',
      '/business-units:POST',
      '/business-units:PUT',
      '/business-units:PUT:BUM',
      '/business-units:DELETE',
      '/projects:VIEW',
      '/projects:PATCH',
      '/products',
      '/products:VIEW',
      '/settings/cloud-reset-password',
      '/products/safescarf/machine-accounts:VIEW',
      '/products/safescarf/product-types:POST',
      '/products/safescarf/product-types:PUT_MEMBERS',
      '/products/safescarf/product-types:PUT',
      '/products/safescarf/product-types:GET_USERS',
      '/products/safescarf/products:POST',
      '/products/safescarf/products:PUT',
      '/products/safescarf/products:PUT_MEMBERS',
      '/products/safescarf/products:DELETE',
      '/products/safescarf/products:GET_USERS',
      '/products/safescarf/users:VIEW',
      '/products/safescarf/users:GET',
      '/products:assign',
    ],
    dynamic: {
      // SAFESCARF
      '/products/safescarf/order': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Safescarf') !== undefined &&
        organization.productIds.find(product => product.type.toLowerCase() === 'safescarf') ===
          undefined,
      '/products/safescarf': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Safescarf') !== undefined,
      '/products/safescarf/users:PUT': (data: TargetUser) => data.userEmail !== data.rowEmail,

      '/users:DELETE': (data: TargetUser) =>
        data.userToBeDeletedEmail !== data.userEmail &&
        AO_AA_BUM_TL_DEV.includes(data.userToBeDeletedRole),
      '/users:PUT': (data: TargetUser) => AA_BUM_TL_DEV.includes(data.userToBeUpdatedRole),
      '/users:PUT_ROLE_CHECK': () => AO_AA_BUM_TL_DEV,
      '/products/compute/order': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Compute') !== undefined,
      '/products/compute/free-trial': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Evaluation') !== undefined,
      '/osprojects:DELETE': (data: TargetUser) =>
        data.osProjectType !== 'computeTrial' && data.userProducts.includes(data.productId),
      '/monitoring': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Monitoring') !== undefined,
    },
  },
  ACCOUNT_ASSISTANT: {
    static: [
      '/organizations:VIEW_OWN_DETAILS',
      '/users:VIEW',
      '/users:POST',
      '/business-units:VIEW',
      '/business-units:POST',
      '/business-units:PUT',
      '/business-units:PUT:BUM',
      '/business-units:DELETE',
      '/projects:VIEW',
      '/projects:PATCH',
      '/products',
      '/settings/cloud-reset-password',
      '/products/safescarf/machine-accounts:VIEW',
      '/products/safescarf/product-types:POST',
      '/products/safescarf/product-types:PUT_MEMBERS',
      '/products/safescarf/product-types:PUT',
      '/products/safescarf/product-types:GET_USERS',
      '/products/safescarf/products:POST',
      '/products/safescarf/products:PUT',
      '/products/safescarf/products:PUT_MEMBERS',
      '/products/safescarf/products:DELETE',
      '/products/safescarf/products:GET_USERS',
      '/products/safescarf/users:VIEW',
      '/products/safescarf/users:GET',
      '/products:assign',
    ],
    dynamic: {
      // SAFESCARF
      '/products/safescarf/order': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Safescarf') !== undefined &&
        organization.productIds.find(product => product.type.toLowerCase() === 'safescarf') ===
          undefined,
      '/products/safescarf': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Safescarf') !== undefined,
      '/products/safescarf/users:PUT': (data: TargetUser) => data.userEmail !== data.rowEmail,

      '/users:DELETE': (data: TargetUser) =>
        data.userToBeDeletedEmail !== data.userEmail &&
        AA_BUM_TL_DEV.includes(data.userToBeDeletedRole),
      '/users:PUT': (data: TargetUser) => BUM_TL_DEV.includes(data.userToBeUpdatedRole),
      '/users:PUT_ROLE_CHECK': () => AA_BUM_TL_DEV,
      '/products/compute/order': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Compute') !== undefined,
      '/products/compute/free-trial': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Evaluation') !== undefined,
      '/osprojects:DELETE': (data: TargetUser) =>
        data.osProjectType !== 'computeTrial' && data.userProducts.includes(data.productId),
      '/monitoring-dashboard': (organizationName: string) =>
        organizationName === MONITORING_ORGANIZATION,
      '/monitoring': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Monitoring') !== undefined,
    },
  },
  BUSINESS_UNIT_MANAGER: {
    static: [
      '/organizations:VIEW_OWN_DETAILS',
      '/users:VIEW',
      '/users:POST',
      '/business-units:PUT',
      '/projects:VIEW',
      '/projects:PATCH',
      '/products',
      '/settings/cloud-reset-password',
      '/products/safescarf/product-types:POST',
      '/products/safescarf/products:POST',
    ],
    dynamic: {
      '/business-units:VIEW': (data: TargetUser) =>
        data.businessUnit.businessUnitManager?._id === data.userId,
      '/users:DELETE': (data: TargetUser) =>
        data.userToBeDeletedEmail !== data.userEmail &&
        BUM_TL_DEV.includes(data.userToBeDeletedRole),
      '/users:PUT_ROLE_CHECK': () => BUM_TL_DEV,
      '/products/compute/order': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Compute') !== undefined,
      '/products/compute/free-trial': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Evaluation') !== undefined,
      '/osprojects:DELETE': (data: TargetUser) =>
        data.osProjectType !== 'computeTrial' && data.userProducts.includes(data.productId),
      '/monitoring-dashboard': (organizationName: string) =>
        organizationName === MONITORING_ORGANIZATION,
      // SAFESCARF
      '/products/safescarf/order': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Safescarf') !== undefined &&
        organization.productIds.find(product => product.type.toLowerCase() === 'safescarf') ===
          undefined,
      '/products/safescarf': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Safescarf') !== undefined,
      '/products/safescarf/machine-accounts:VIEW': (data: TargetUser) =>
        data.createdBy === data.userEmail,
      '/products/safescarf/product-types:PUT': ({ safescarfUser, productType }: SafescarfRule) =>
        safescarfUser.globalRole === 2 || productType.members[safescarfUser.id]?.role === 2,
      '/products/safescarf/product-types:PUT_MEMBERS': (safescarfUser: SafeScarfUser) =>
        safescarfUser.globalRole === 2 || safescarfUser.productTypeMemberRole === 2,
      '/products/safescarf/product-types:GET_USERS': ({
        safescarfUser,
        productType,
      }: SafescarfRule) =>
        safescarfUser.globalRole === 2 || productType.members[safescarfUser.id]?.role === 2,
      '/products/safescarf/products:PUT': ({ product, safescarfUser }: SafescarfRule) =>
        safescarfUser.globalRole === 3 || product.members[safescarfUser.id]?.role === 3,
      '/products/safescarf/products:PUT_MEMBERS': (safescarfUser: SafeScarfUser) =>
        safescarfUser.globalRole === 3 || safescarfUser.productMemberRole === 3,
      '/products/safescarf/products:GET_USERS': ({ safescarfUser, product }: SafescarfRule) =>
        safescarfUser.globalRole === 3 || product.members[safescarfUser.id]?.role === 3,
      '/monitoring': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Monitoring') !== undefined,
    },
  },
  TECHNICAL_LEAD: {
    static: [
      '/organizations:VIEW_OWN_DETAILS',
      '/products',
      '/projects:VIEW',
      '/products:PUT',
      '/settings/cloud-reset-password',
      '/products/safescarf/product-types:POST',
      '/products/safescarf/products:POST',
    ],
    dynamic: {
      '/users:DELETE': (data: TargetUser) =>
        data.userToBeDeletedEmail !== data.userEmail && TL_DEV.includes(data.userToBeDeletedRole),
      '/users:PUT_ROLE_CHECK': () => TL_DEV,
      '/products/compute/order': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Compute') !== undefined,
      '/products/compute/free-trial': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Evaluation') !== undefined,
      '/osprojects:DELETE': (data: TargetUser) =>
        data.osProjectType !== 'computeTrial' && data.userProducts.includes(data.productId),
      '/monitoring-dashboard': (organizationName: string) =>
        organizationName === MONITORING_ORGANIZATION,
      // SAFESCARF
      '/products/safescarf/order': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Safescarf') !== undefined &&
        organization.productIds.find(product => product.type.toLowerCase() === 'safescarf') ===
          undefined,
      '/products/safescarf': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Safescarf') !== undefined,
      '/products/safescarf/machine-accounts:VIEW': (data: TargetUser) =>
        data.createdBy === data.userEmail,
      '/products/safescarf/product-types:PUT': ({ safescarfUser, productType }: SafescarfRule) =>
        safescarfUser.globalRole === 2 || productType.members[safescarfUser.id]?.role === 2,
      '/products/safescarf/product-types:PUT_MEMBERS': (safescarfUser: SafeScarfUser) =>
        safescarfUser.globalRole === 2 || safescarfUser.productTypeMemberRole === 2,
      '/products/safescarf/product-types:GET_USERS': ({
        safescarfUser,
        productType,
      }: SafescarfRule) =>
        safescarfUser.globalRole === 2 || productType.members[safescarfUser.id]?.role === 2,
      '/products/safescarf/products:PUT': ({ product, safescarfUser }: SafescarfRule) =>
        safescarfUser.globalRole === 3 || product.members[safescarfUser.id]?.role === 3,
      '/products/safescarf/products:PUT_MEMBERS': (safescarfUser: SafeScarfUser) =>
        safescarfUser.globalRole === 3 || safescarfUser.productMemberRole === 3,
      '/products/safescarf/products:GET_USERS': ({ safescarfUser, product }: SafescarfRule) =>
        safescarfUser.globalRole === 3 || product.members[safescarfUser.id]?.role === 3,
      '/monitoring': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Monitoring') !== undefined,
    },
  },
  DEVELOPER: {
    static: [
      '/organizations:VIEW_OWN_DETAILS',
      '/projects:VIEW',
      '/products',
      '/settings/cloud-reset-password',
      '/products/safescarf/product-types:POST',
      '/products/safescarf/products:POST',
    ],
    dynamic: {
      '/products/compute/order': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Compute') !== undefined,
      '/products/compute/free-trial': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Evaluation') !== undefined,
      '/osprojects:DELETE': (data: TargetUser) =>
        data.osProjectType !== 'computeTrial' && data.userProducts.includes(data.productId),
      '/monitoring-dashboard': (organizationName: string) =>
        organizationName === MONITORING_ORGANIZATION,
      // SAFESCARF
      '/products/safescarf/order': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Safescarf') !== undefined &&
        organization.productIds.find(product => product.type.toLowerCase() === 'safescarf') ===
          undefined,
      '/products/safescarf': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Safescarf') !== undefined,
      '/products/safescarf/machine-accounts:VIEW': (data: TargetUser) =>
        data.createdBy === data.userEmail,
      '/products/safescarf/product-types:PUT': ({ safescarfUser, productType }: SafescarfRule) =>
        safescarfUser.globalRole === 2 || productType.members[safescarfUser.id]?.role === 2,
      '/products/safescarf/product-types:PUT_MEMBERS': (safescarfUser: SafeScarfUser) =>
        safescarfUser.globalRole === 2 || safescarfUser.productTypeMemberRole === 2,
      '/products/safescarf/product-types:GET_USERS': ({
        safescarfUser,
        productType,
      }: SafescarfRule) =>
        safescarfUser.globalRole === 2 || productType.members[safescarfUser.id]?.role === 2,
      '/products/safescarf/products:PUT': ({ product, safescarfUser }: SafescarfRule) =>
        safescarfUser.globalRole === 3 || product.members[safescarfUser.id]?.role === 3,
      '/products/safescarf/products:PUT_MEMBERS': (safescarfUser: SafeScarfUser) =>
        safescarfUser.globalRole === 3 || safescarfUser.productMemberRole === 3,
      '/products/safescarf/products:GET_USERS': ({ safescarfUser, product }: SafescarfRule) =>
        safescarfUser.globalRole === 3 || product.members[safescarfUser.id]?.role === 3,
      '/monitoring': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Monitoring') !== undefined,
    },
  },
};

export default rules;
